import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  Chat,
  Close,
  Schedule,
  Sync,
  CheckCircle,
  Replay,
  ErrorOutline,
  Cancel,
} from "@mui/icons-material";
import moment from "moment-timezone";
import { GetPackageWhatsAppLogs } from "api/packages";
import { useTranslation } from "react-i18next";
import KpiTile from "components/bento/KpiTile";
import EmptyState from "components/bento/EmptyState";
import {
  MONO,
  TEXT_DARK,
  TEXT_BODY,
  TEXT_SECONDARY,
  TEXT_SUBTLE,
  BG_PURPLE_GRADIENT,
  BORDER,
  BORDER_PURPLE,
  DIVIDER,
  PURPLE_MAIN,
  PURPLE_DARK,
  PURPLE_TINT_100,
  SUCCESS_FG,
  SUCCESS_BG,
  ERROR_FG,
  ERROR_BG,
  RADIUS_CARD,
  RADIUS_TILE,
  cardLabelSx,
} from "components/bento/bentoTokens";

// Per-status visual identity for the job status badge/icon — separate palette
// from PackageEditLogsDialog's ACTION_META since WhatsAppMessageJob.status is
// its own enum (queued/processing/sent/retry_scheduled/failed/...).
const STATUS_META = {
  pending: { color: TEXT_SECONDARY, badgeBg: "#f8f7fa", badgeCol: TEXT_SECONDARY, badgeBorder: BORDER, Icon: Schedule },
  scheduled: { color: TEXT_SECONDARY, badgeBg: "#f8f7fa", badgeCol: TEXT_SECONDARY, badgeBorder: BORDER, Icon: Schedule },
  processing: { color: PURPLE_MAIN, badgeBg: PURPLE_TINT_100, badgeCol: PURPLE_DARK, badgeBorder: BORDER_PURPLE, Icon: Sync },
  retry_scheduled: { color: "#b45309", badgeBg: "#fef3c7", badgeCol: "#b45309", badgeBorder: "#fde68a", Icon: Replay },
  sent: { color: SUCCESS_FG, badgeBg: SUCCESS_BG, badgeCol: SUCCESS_FG, badgeBorder: "#86efac", Icon: CheckCircle },
  partially_sent: { color: "#b45309", badgeBg: "#fef3c7", badgeCol: "#b45309", badgeBorder: "#fde68a", Icon: CheckCircle },
  failed: { color: ERROR_FG, badgeBg: ERROR_BG, badgeCol: ERROR_FG, badgeBorder: "#fca5a5", Icon: ErrorOutline },
  cancelled: { color: TEXT_SUBTLE, badgeBg: "#f8f7fa", badgeCol: TEXT_SUBTLE, badgeBorder: BORDER, Icon: Cancel },
};
const DEFAULT_STATUS_META = STATUS_META.pending;

function StatusBadge({ status, t }) {
  const meta = STATUS_META[status] || DEFAULT_STATUS_META;
  const { Icon } = meta;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "12px",
        fontWeight: 700, px: "11px", py: "5px", borderRadius: "999px",
        background: meta.badgeBg, color: meta.badgeCol, border: `1px solid ${meta.badgeBorder}`,
      }}
    >
      <Icon sx={{ fontSize: 13 }} />
      {t(`packageWhatsAppLogs.status.${status}`, status)}
    </Box>
  );
}

function formatTime(value) {
  return value ? moment(value).tz("Asia/Vientiane").format("DD/MM/YYYY · HH:mm:ss") : null;
}

function AttemptRow({ attempt }) {
  const failed = attempt.outcome === "failed" || attempt.outcome === "timeout";
  return (
    <Box
      sx={{
        display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px",
        p: "9px 14px", borderBottom: `1px solid ${DIVIDER}`, "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Typography sx={{ fontFamily: MONO, fontSize: "12px", fontWeight: 600, color: TEXT_SECONDARY }}>
          #{attempt.attemptNumber}
        </Typography>
        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: failed ? ERROR_FG : SUCCESS_FG }}>
          {attempt.outcome}
        </Typography>
      </Box>
      <Box sx={{ textAlign: "right", minWidth: 0 }}>
        {attempt.errorMessage && (
          <Typography sx={{ fontSize: "12px", color: ERROR_FG }}>{attempt.errorMessage}</Typography>
        )}
        <Typography sx={{ fontFamily: MONO, fontSize: "11px", color: TEXT_SUBTLE }}>
          {formatTime(attempt.finishedAt || attempt.startedAt)}
        </Typography>
      </Box>
    </Box>
  );
}

const PackageWhatsAppLogsDialog = ({ open, onClose, packageId, tracking }) => {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && packageId) {
      setLoading(true);
      GetPackageWhatsAppLogs(packageId).then((res) => {
        setJobs(res.data || []);
        setLoading(false);
      });
    }
  }, [open, packageId]);

  const sentCount = useMemo(() => jobs.filter((j) => j.status === "sent").length, [jobs]);
  const failedCount = useMemo(
    () => jobs.filter((j) => ["failed", "partially_sent"].includes(j.status)).length,
    [jobs]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: RADIUS_CARD, overflow: "hidden" } }}>
      <DialogTitle sx={{ p: "24px 28px 20px" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box
              sx={{
                width: 40, height: 40, borderRadius: "12px", flexShrink: 0,
                background: BG_PURPLE_GRADIENT, border: `1px solid ${BORDER_PURPLE}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Chat sx={{ fontSize: 19, color: PURPLE_MAIN }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "20px", fontWeight: 800, color: TEXT_DARK, letterSpacing: "-.01em" }}>
                {t("packageWhatsAppLogs.title")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "2px", flexWrap: "wrap" }}>
                <Typography sx={{ fontFamily: MONO, fontSize: "13px", fontWeight: 600, color: PURPLE_DARK }}>
                  {tracking}
                </Typography>
                {!loading && (
                  <Typography sx={{ fontSize: "12px", color: TEXT_SUBTLE }}>
                    · {jobs.length} {jobs.length === 1 ? "message" : "messages"}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<Close sx={{ fontSize: 14 }} />}
            onClick={onClose}
            sx={{ borderColor: BORDER_PURPLE, borderRadius: "12px", fontWeight: 700, fontSize: "13px", px: "16px" }}
          >
            {t("common.close")}
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: "0 28px 28px" }}>
        {loading && (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress color="secondary" />
          </Box>
        )}

        {!loading && jobs.length === 0 && (
          <EmptyState icon={<Chat sx={{ fontSize: 28, color: TEXT_SUBTLE }} />} message={t("packageWhatsAppLogs.noLogs")} />
        )}

        {!loading && jobs.length > 0 && (
          <>
            {/* ══ STATS STRIP ══ */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px", mb: "24px" }}>
              <Box sx={{ background: BG_PURPLE_GRADIENT, border: `1px solid ${BORDER_PURPLE}`, borderRadius: RADIUS_TILE, p: "16px 20px" }}>
                <Box sx={cardLabelSx}>Total Messages</Box>
                <Typography sx={{ fontFamily: MONO, fontSize: 28, fontWeight: 700, color: PURPLE_DARK, mt: "6px" }}>
                  {jobs.length}
                </Typography>
              </Box>
              <KpiTile label="Sent" value={sentCount} valueColor={SUCCESS_FG} />
              <KpiTile label="Failed" value={failedCount} valueColor={failedCount > 0 ? ERROR_FG : TEXT_DARK} />
            </Box>

            {/* ══ TIMELINE ══ */}
            <Box>
              {jobs.map((job, index) => {
                const hasLine = index < jobs.length - 1;
                const attempts = job.attempts?.history || [];

                return (
                  <Box key={job.id} sx={{ display: "flex", gap: "18px" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 48 }}>
                      <Box
                        sx={{
                          width: 48, height: 48, borderRadius: "50%", flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          background: (STATUS_META[job.status] || DEFAULT_STATUS_META).color,
                          boxShadow: `0 4px 14px -4px ${(STATUS_META[job.status] || DEFAULT_STATUS_META).color}99`,
                        }}
                      >
                        <Chat sx={{ fontSize: 18, color: "#fff" }} />
                      </Box>
                      {hasLine && (
                        <Box sx={{ width: "2px", flex: 1, minHeight: 20, background: `linear-gradient(180deg, ${BORDER_PURPLE} 0%, transparent 100%)`, my: "4px", borderRadius: "2px" }} />
                      )}
                    </Box>

                    <Box sx={{ flex: 1, pb: "20px", minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", mb: "10px", flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap" }}>
                          <StatusBadge status={job.status} t={t} />
                          <Typography sx={{ fontFamily: MONO, fontSize: "12px", color: TEXT_SECONDARY, fontWeight: 500 }}>
                            {job.messageType}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontFamily: MONO, fontSize: "12px", color: PURPLE_MAIN, fontWeight: 600 }}>
                          {job.destination?.phoneNumber}
                        </Typography>
                      </Box>

                      <Box sx={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "16px", overflow: "hidden" }}>
                        <Box sx={{ p: "10px 14px", borderBottom: attempts.length ? `1px solid ${DIVIDER}` : "none", display: "flex", gap: "18px", flexWrap: "wrap" }}>
                          {job.schedule?.queuedAt && (
                            <Box>
                              <Box sx={{ ...cardLabelSx, fontSize: "9.5px" }}>Queued</Box>
                              <Typography sx={{ fontFamily: MONO, fontSize: "12px", color: TEXT_BODY }}>{formatTime(job.schedule.queuedAt)}</Typography>
                            </Box>
                          )}
                          {job.schedule?.sentAt && (
                            <Box>
                              <Box sx={{ ...cardLabelSx, fontSize: "9.5px" }}>Sent</Box>
                              <Typography sx={{ fontFamily: MONO, fontSize: "12px", color: SUCCESS_FG }}>{formatTime(job.schedule.sentAt)}</Typography>
                            </Box>
                          )}
                          {job.schedule?.failedAt && (
                            <Box>
                              <Box sx={{ ...cardLabelSx, fontSize: "9.5px" }}>Failed</Box>
                              <Typography sx={{ fontFamily: MONO, fontSize: "12px", color: ERROR_FG }}>{formatTime(job.schedule.failedAt)}</Typography>
                            </Box>
                          )}
                          {job.result?.lastErrorMessage && (
                            <Box sx={{ flexBasis: "100%" }}>
                              <Typography sx={{ fontSize: "12px", color: ERROR_FG }}>
                                {job.result.lastErrorMessage}
                                {job.result.failureReasonCategory ? ` (${job.result.failureReasonCategory})` : ""}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        {attempts.map((attempt, i) => (
                          <AttemptRow key={`${job.id}-${i}`} attempt={attempt} />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PackageWhatsAppLogsDialog;
